import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Table from "../../../core/common/dataTable/index";
import { useDispatch } from "react-redux";
import CollapseHeader from "../../../core/common/collapse-header";
import { all_routes } from "../../router/all_routes";
import { useOrigens } from "../../../hooks/useOrigens";
import { apiService } from "../../../services/api";
import { Origem, CreateOrigemData } from '../../../types/origens';
import { Offcanvas, Modal } from 'bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import { ProtectedComponent } from "../../../components/ProtectedComponent";

const route = all_routes;

interface TableColumn {
  title: string;
  dataIndex?: string;
  render?: (text: any, record: Origem) => React.ReactNode;
  sorter?: (a: Origem, b: Origem) => number;
}

export const Origens = () => {
  const dispatch = useDispatch();
  const { origens, loading, error, refetchOrigens } = useOrigens();
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deletingOrigemId, setDeletingOrigemId] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [filteredOrigens, setFilteredOrigens] = useState<typeof origens>([]);
  const [isSearching, setIsSearching] = useState(false);

  // Estado para controlar a origem em edição
  const [editingOrigem, setEditingOrigem] = useState<Origem | null>(null);
  const [newOrigem, setNewOrigem] = useState<CreateOrigemData>({
    nome: '',
    descricao: ''
  });

  // Efeito para inicializar a lista filtrada
  useEffect(() => {
    setFilteredOrigens(origens);
  }, [origens]);

  // Função para filtrar origens quando o texto de busca muda (com debounce)
  useEffect(() => {
    setIsSearching(true);
    const debounceTimeout = setTimeout(() => {
      if (!searchText.trim()) {
        setFilteredOrigens(origens);
        setIsSearching(false);
        return;
      }

      const searchTerms = searchText.toLowerCase().trim().split(' ');
      
      const filtered = origens.filter(origem => {
        // Campos a serem pesquisados
        const searchableFields = [
          origem.nome,
          origem.descricao,
          origem.ativo ? 'inativo' : 'ativo'
        ].filter(Boolean).map(field => field?.toLowerCase() || '');

        // Verificar se todos os termos de pesquisa estão presentes em pelo menos um dos campos
        return searchTerms.every(term => 
          searchableFields.some(field => field.includes(term))
        );
      });

      setFilteredOrigens(filtered);
      setIsSearching(false);
    }, 300); // 300ms de delay para debounce

    return () => clearTimeout(debounceTimeout);
  }, [searchText, origens]);

  // Função para limpar texto de pesquisa
  const handleClearSearch = () => {
    setSearchText('');
  };

  // Função para limpar o estado de edição
  const handleCancelEdit = () => {
    setEditingOrigem(null);
  };

  // Função para limpar o estado da nova origem
  const handleCancelNew = () => {
    setNewOrigem({
      nome: '',
      descricao: ''
    });
  };

  // Função para preparar a edição
  const handleEdit = (origem: Origem) => {
    setEditingOrigem(origem);
  };

  // Função para preparar a exclusão
  const handlePrepareDelete = (origem: Origem) => {
    setDeletingOrigemId(origem.id);
  };

  const columns: TableColumn[] = [
    {
      title: "Nome",
      dataIndex: "nome",
      sorter: (a, b) => a.nome.localeCompare(b.nome),
    },
    {
      title: "Descrição",
      dataIndex: "descricao",
      render: (_, record) => (
        <div>
          {record.descricao || '-'}
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "ativo",
      render: (ativo) => (
        <div>
          {ativo ? (
            <span className="badge badge-pill badge-status bg-danger">Inativo</span>
          ) : (
            <span className="badge badge-pill badge-status bg-success">Ativo</span>
          )}
        </div>
      ),
      sorter: (a, b) => {
        const aValue = a.ativo ? 1 : 0;
        const bValue = b.ativo ? 1 : 0;
        return aValue - bValue;
      },
    },
    {
      title: "Ações",
      render: (_, record) => (
        <div>
          <ProtectedComponent module="CONFIG_ORIGENS" action="EDITAR">
            <Link
              to="#"
              className="dropdown-item"
              data-bs-toggle="offcanvas"
              data-bs-target="#offcanvas_edit"
              onClick={() => handleEdit(record)}
            >
              <i className="ti ti-edit text-blue"></i> Editar
            </Link>
          </ProtectedComponent>
          <ProtectedComponent module="CONFIG_ORIGENS" action="EXCLUIR">
            <Link
              to="#"
              className="dropdown-item"
              data-bs-toggle="modal"
              data-bs-target="#delete_origem"
              onClick={() => handlePrepareDelete(record)}
            >
              <i className="ti ti-trash text-danger"></i> Excluir
            </Link>
          </ProtectedComponent>
        </div>
      ),
    }
  ];

  // Função para limpar modal
  const limparModal = (idModal: string) => {
    const elementoModal = document.getElementById(idModal);
    const instanciaModal = elementoModal ? Modal.getInstance(elementoModal) : null;

    if (instanciaModal) {
      instanciaModal.hide();
    }

    const executarLimpeza = () => {
      document.body.style.removeProperty('overflow');
      document.body.style.removeProperty('padding-right');
      document.body.classList.remove('modal-open', 'offcanvas-open');

      const backdrops = document.querySelectorAll('.modal-backdrop, .offcanvas-backdrop');
      backdrops.forEach(backdrop => backdrop.remove());

      if (idModal === 'success_delete_modal') {
        setDeletingOrigemId(null);
      }
    };

    if (elementoModal?.classList.contains('show')) {
      elementoModal.addEventListener('hidden.bs.modal', () => {
        executarLimpeza();
      }, { once: true });
    } else {
      executarLimpeza();
    }
  };

  // Função para criar nova origem
  const handleCreateOrigem = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setIsSubmitting(true);

      if (!newOrigem.nome.trim()) {
        alert('O nome é obrigatório');
        return;
      }

      await apiService.createOrigem(newOrigem);
      await refetchOrigens();

      handleCancelNew();

      // Fecha o offcanvas
      const offcanvasElement = document.getElementById('offcanvas_origem');
      if (offcanvasElement) {
        const offcanvasInstance = Offcanvas.getInstance(offcanvasElement);
        if (offcanvasInstance) {
          offcanvasInstance.hide();
        }
      }

      // Mostra modal de sucesso
      setTimeout(() => {
        const successModal = document.getElementById('success_modal');
        if (successModal) {
          const modalInstance = new Modal(successModal);
          modalInstance.show();
        }
      }, 300);

    } catch (error) {
      console.error('Erro ao criar origem:', error);
      alert('Erro ao criar a origem. Por favor, tente novamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Função para atualizar origem
  const handleUpdateOrigem = async () => {
    if (!editingOrigem?.id) return;

    try {
      setIsSubmitting(true);
      await apiService.updateOrigem(editingOrigem.id, editingOrigem);
      await refetchOrigens();

      // Fecha o offcanvas
      const offcanvasElement = document.getElementById('offcanvas_edit');
      if (offcanvasElement) {
        const offcanvasInstance = Offcanvas.getInstance(offcanvasElement);
        if (offcanvasInstance) {
          offcanvasInstance.hide();
        }
      }

      // Mostra modal de sucesso
      setTimeout(() => {
        const successModal = document.getElementById('success_edit_modal');
        if (successModal) {
          const modalInstance = new Modal(successModal);
          modalInstance.show();
        }
      }, 300);

      handleCancelEdit();
    } catch (error) {
      console.error('Erro ao atualizar origem:', error);
      alert('Erro ao atualizar a origem. Por favor, tente novamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Função para deletar origem
  const handleDeleteOrigem = async () => {
    if (!deletingOrigemId) return;

    try {
      setIsDeleting(true);
      await apiService.deleteOrigem(deletingOrigemId);
      await refetchOrigens();

      // Fecha o modal de confirmação
      const modalElement = document.getElementById('delete_origem');
      if (modalElement) {
        const modalInstance = Modal.getInstance(modalElement);
        if (modalInstance) {
          modalInstance.hide();
        }
      }

      // Mostra modal de sucesso
      setTimeout(() => {
        const successModal = document.getElementById('success_delete_modal');
        if (successModal) {
          const modalInstance = new Modal(successModal);
          modalInstance.show();
        }
      }, 300);

    } catch (error) {
      console.error('Erro ao excluir origem:', error);
      alert('Erro ao excluir a origem. Por favor, tente novamente.');
    } finally {
      setIsDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <div className="col-md-12">
              <div className="card">
                <div className="card-body d-flex justify-content-center align-items-center" style={{ minHeight: "300px" }}>
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Carregando...</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <div className="col-md-12">
              <div className="card">
                <div className="card-body">
                  <div className="alert alert-danger" role="alert">
                    {error}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <div className="col-md-12">
              {/* Cabeçalho da Página */}
              <div className="page-header">
                <div className="row align-items-center">
                  <div className="col-4">
                    <h4 className="page-title">
                      Origens<span className="count-title">{filteredOrigens.length} origens</span>
                    </h4>
                  </div>
                  <div className="col-8 text-end">
                    <div className="head-icons">
                      <CollapseHeader />
                    </div>
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="card-header">
                  <div className="row align-items-center">
                    <div className="col-sm-4">
                      <div className="search-input position-relative">
                        <div className="icon-form mb-3 mb-sm-0">
                          <span className="form-icon">
                            {isSearching ? (
                              <i className="spinner-border spinner-border-sm" role="status" />
                            ) : (
                              <i className="ti ti-search" />
                            )}
                          </span>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Pesquisar Origem"
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                          />
                          {searchText && (
                            <button 
                              className="btn btn-sm position-absolute" 
                              style={{ right: "10px", top: "50%", transform: "translateY(-50%)" }}
                              onClick={handleClearSearch}
                              type="button"
                            >
                              <i className="ti ti-x" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="card-body">
                  {/* Filtros e Ações */}
                  <div className="d-flex align-items-center justify-content-between flex-wrap row-gap-2 mb-4">
                    <div className="d-flex align-items-center flex-wrap row-gap-2">
                      {/* Botão Ordenar */}
                      <div className="dropdown me-2">
                        <Link to="#" className="dropdown-toggle" data-bs-toggle="dropdown">
                          <i className="ti ti-sort-ascending-2 me-2" />
                          Ordenar
                        </Link>
                        <div className="dropdown-menu">
                          <Link to="#" className="dropdown-item">
                            <i className="ti ti-circle-chevron-right me-1" />
                            Nome A-Z
                          </Link>
                          <Link to="#" className="dropdown-item">
                            <i className="ti ti-circle-chevron-right me-1" />
                            Nome Z-A
                          </Link>
                        </div>
                      </div>

                      {/* Botão Exportar */}
                      <div className="dropdown">
                        <Link to="#" className="dropdown-toggle" data-bs-toggle="dropdown">
                          <i className="ti ti-package-export me-2" />
                          Exportar
                        </Link>
                        <div className="dropdown-menu dropdown-menu-end">
                          <Link to="#" className="dropdown-item">
                            <i className="ti ti-file-type-pdf text-danger me-1" />
                            Exportar como PDF
                          </Link>
                          <Link to="#" className="dropdown-item">
                            <i className="ti ti-file-type-xls text-green me-1" />
                            Exportar como Excel
                          </Link>
                        </div>
                      </div>
                    </div>

                    {/* Botões de Ação */}
                    <div className="d-flex align-items-center flex-wrap row-gap-2">
                      <ProtectedComponent module="CONFIG_ORIGENS" action="CRIAR">
                        <Link
                          to="#"
                          className="btn btn-primary"
                          data-bs-toggle="offcanvas"
                          data-bs-target="#offcanvas_origem"
                        >
                          <i className="ti ti-plus me-2" />
                          Adicionar Origem
                        </Link>
                      </ProtectedComponent>
                    </div>
                  </div>

                  {/* Tabela de Origens */}
                  <div className="table-responsive custom-table">
                    {!isSearching && searchText && filteredOrigens.length === 0 ? (
                      <div className="text-center py-5">
                        <div className="mb-3">
                          <i className="ti ti-search-off fs-3 text-muted"></i>
                        </div>
                        <h5 className="text-muted">Nenhuma origem encontrada</h5>
                        <p className="text-muted">Sua pesquisa por "{searchText}" não retornou resultados.</p>
                        <button 
                          className="btn btn-outline-primary mt-2" 
                          onClick={handleClearSearch}
                        >
                          <i className="ti ti-x me-1"></i>
                          Limpar pesquisa
                        </button>
                      </div>
                    ) : (
                      <Table 
                        dataSource={filteredOrigens.map(origem => ({
                          ...origem,
                          key: origem.id.toString()
                        }))} 
                        columns={columns} 
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Offcanvas Adicionar Nova Origem */}
      <div className="offcanvas offcanvas-end offcanvas-large" tabIndex={-1} id="offcanvas_origem">
        <div className="offcanvas-header border-bottom">
          <h4>Adicionar Nova Origem</h4>
          <button type="button" className="btn-close" data-bs-dismiss="offcanvas">
            <i className="ti ti-x" />
          </button>
        </div>
        <div className="offcanvas-body">
          <form onSubmit={handleCreateOrigem}>
            <div className="mb-3">
              <label className="col-form-label">
                Nome <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                value={newOrigem.nome}
                onChange={(e) => setNewOrigem(prev => ({
                  ...prev,
                  nome: e.target.value
                }))}
              />
            </div>

            <div className="mb-3">
              <label className="col-form-label">Descrição</label>
              <textarea
                className="form-control"
                value={newOrigem.descricao || ''}
                onChange={(e) => setNewOrigem(prev => ({
                  ...prev,
                  descricao: e.target.value
                }))}
              />
            </div>

            <div className="d-flex align-items-center justify-content-end">
              <button
                type="button"
                className="btn btn-light me-2"
                data-bs-dismiss="offcanvas"
                disabled={isSubmitting}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Criando...
                  </>
                ) : (
                  'Criar'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Offcanvas Editar Origem */}
      <div className="offcanvas offcanvas-end offcanvas-large" tabIndex={-1} id="offcanvas_edit">
        <div className="offcanvas-header border-bottom">
          <h4>Editar Origem</h4>
          <button
            type="button"
            className="btn-close"
            data-bs-dismiss="offcanvas"
            onClick={handleCancelEdit}
          >
            <i className="ti ti-x" />
          </button>
        </div>
        <div className="offcanvas-body">
          <form onSubmit={(e) => {
            e.preventDefault();
            handleUpdateOrigem();
          }}>
            <div className="mb-3">
              <label className="col-form-label">
                Nome <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                value={editingOrigem?.nome || ''}
                onChange={(e) => setEditingOrigem(prev => prev ? {
                  ...prev,
                  nome: e.target.value
                } : null)}
              />
            </div>

            <div className="mb-3">
              <label className="col-form-label">Descrição</label>
              <textarea
                className="form-control"
                value={editingOrigem?.descricao || ''}
                onChange={(e) => setEditingOrigem(prev => prev ? {
                  ...prev,
                  descricao: e.target.value
                } : null)}
              />
            </div>

            <div className="d-flex align-items-center justify-content-end">
              <button
                type="button"
                className="btn btn-light me-2"
                data-bs-dismiss="offcanvas"
                onClick={handleCancelEdit}
                disabled={isSubmitting}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Salvando...
                  </>
                ) : (
                  'Salvar Alterações'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Modal de Confirmação de Exclusão */}
      <div className="modal fade" id="delete_origem" role="dialog">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-body">
              <div className="text-center">
                <div className="avatar avatar-xl bg-danger-light rounded-circle mb-3">
                  <i className="ti ti-trash-x fs-36 text-danger" />
                </div>
                <h4 className="mb-2">Remover Origem?</h4>
                <p className="mb-0">
                  Tem certeza que deseja remover a origem selecionada?
                </p>
                <div className="d-flex align-items-center justify-content-center mt-4">
                  <button
                    type="button"
                    className="btn btn-light me-2"
                    data-bs-dismiss="modal"
                    disabled={isDeleting}
                    onClick={() => setDeletingOrigemId(null)}
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={handleDeleteOrigem}
                    disabled={isDeleting}
                  >
                    {isDeleting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Excluindo...
                      </>
                    ) : (
                      'Sim, Deletar'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de Sucesso - Criação */}
      <div className="modal fade custom-modal" id="success_modal" tabIndex={-1} role="dialog" aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-body">
              <div className="success-message text-center">
                <div className="avatar avatar-xl bg-success-light rounded-circle mb-3">
                  <i className="ti ti-check-check fs-36 text-success" />
                </div>
                <h3>Origem Criada com Sucesso!</h3>
                <p>A origem foi criada e já está disponível para uso.</p>
                <div className="d-flex align-items-center justify-content-center mt-4">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => limparModal('success_modal')}
                  >
                    OK
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de Sucesso - Edição */}
      <div className="modal fade custom-modal" id="success_edit_modal" tabIndex={-1} role="dialog" aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-body">
              <div className="success-message text-center">
                <div className="avatar avatar-xl bg-success-light rounded-circle mb-3">
                  <i className="ti ti-check-check fs-36 text-success" />
                </div>
                <h3>Origem Atualizada com Sucesso!</h3>
                <p>As alterações foram salvas com sucesso.</p>
                <div className="d-flex align-items-center justify-content-center mt-4">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => limparModal('success_edit_modal')}
                  >
                    OK
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de Sucesso - Exclusão */}
      <div className="modal fade custom-modal" id="success_delete_modal" tabIndex={-1} role="dialog" aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-body">
              <div className="success-message text-center">
                <div className="avatar avatar-xl bg-success-light rounded-circle mb-3">
                  <i className="ti ti-check-check fs-36 text-success" />
                </div>
                <h3>Origem Excluída com Sucesso!</h3>
                <p>A origem foi removida permanentemente do sistema.</p>
                <div className="d-flex align-items-center justify-content-center mt-4">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => limparModal('success_delete_modal')}
                  >
                    OK
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};